/**
 * MEVIA - HR Indicators Panel Component
 * Reusable component for displaying HR indicators inline within tabs
 * Supports different contexts: Culture, Personality, Integrated
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertTriangle, TrendingUp, Users, Shield, Activity, Target, Heart,
  UserCheck, UserX, ChevronDown, ChevronUp, Loader2, RefreshCw,
  BarChart3, Info, AlertCircle, CheckCircle, Brain, Briefcase, Eye,
  UserMinus, Handshake, Crosshair, Sprout, Palette, Gauge, Award,
  Check, X, AlertOctagon
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import IPSGauge, { IPSSummary } from './IPSGauge';
import logger from '../../utils/logger';

// Indicator configurations with colors and interpretations
const INDICATOR_CONFIG = {
  irr: {
    name: { es: 'Riesgo de Rotación', en: 'Rotation Risk' },
    shortName: { es: 'IRR', en: 'IRR' },
    icon: UserX,
    colorGood: '#22C55E',
    colorBad: '#EF4444',
    invertedScale: true, // Lower is better
    description: { 
      es: 'Probabilidad de que el empleado abandone la organización',
      en: 'Probability of the employee leaving the organization'
    }
  },
  ico: {
    name: { es: 'Compromiso', en: 'Commitment' },
    shortName: { es: 'ICO', en: 'ICO' },
    icon: Heart,
    colorGood: '#3B82F6',
    colorBad: '#EF4444',
    invertedScale: false,
    description: { 
      es: 'Nivel de conexión y compromiso con la organización',
      en: 'Level of connection and commitment to the organization'
    }
  },
  iap: {
    name: { es: 'Adecuación al Puesto', en: 'Job Fit' },
    shortName: { es: 'IAP', en: 'IAP' },
    icon: Target,
    colorGood: '#8B5CF6',
    colorBad: '#EF4444',
    invertedScale: false,
    description: { 
      es: 'Qué tan bien el perfil se ajusta a los requerimientos del puesto',
      en: 'How well the profile fits the job requirements'
    }
  },
  ipc: {
    name: { es: 'Potencial de Crecimiento', en: 'Growth Potential' },
    shortName: { es: 'IPC', en: 'IPC' },
    icon: TrendingUp,
    colorGood: '#22C55E',
    colorBad: '#F59E0B',
    invertedScale: false,
    description: { 
      es: 'Capacidad de desarrollo profesional',
      en: 'Professional development capacity'
    }
  },
  icf: {
    name: { es: 'Cultural Fit', en: 'Cultural Fit' },
    shortName: { es: 'ICF', en: 'ICF' },
    icon: Users,
    colorGood: '#6366F1',
    colorBad: '#EF4444',
    invertedScale: false,
    description: { 
      es: 'Compatibilidad cultural con la organización',
      en: 'Cultural compatibility with the organization'
    }
  },
  ipe: {
    name: { es: 'Productividad Esperada', en: 'Expected Productivity' },
    shortName: { es: 'IPE', en: 'IPE' },
    icon: Activity,
    colorGood: '#06B6D4',
    colorBad: '#EF4444',
    invertedScale: false,
    description: { 
      es: 'Nivel de productividad predecido basado en el perfil',
      en: 'Predicted productivity level based on profile'
    }
  },
  ilo: {
    name: { es: 'Lealtad', en: 'Loyalty' },
    shortName: { es: 'ILO', en: 'ILO' },
    icon: Shield,
    colorGood: '#14B8A6',
    colorBad: '#EF4444',
    invertedScale: false,
    description: { 
      es: 'Probabilidad de permanencia y lealtad',
      en: 'Likelihood of retention and loyalty'
    }
  }
};

// Alert type configurations
const ALERT_CONFIG = {
  RIESGO_FINANCIERO: {
    name: { es: 'Riesgo Financiero', en: 'Financial Risk' },
    icon: Briefcase,
    color: '#F97316',
    severity: 'ALTA'
  },
  CRISIS_PROPOSITO: {
    name: { es: 'Crisis de Propósito', en: 'Purpose Crisis' },
    icon: Target,
    color: '#8B5CF6',
    severity: 'ALTA'
  },
  RIESGO_BURNOUT: {
    name: { es: 'Riesgo de Burnout', en: 'Burnout Risk' },
    icon: Brain,
    color: '#EF4444',
    severity: 'MEDIA'
  },
  DESAJUSTE_CULTURAL: {
    name: { es: 'Desajuste Cultural', en: 'Cultural Mismatch' },
    icon: Users,
    color: '#3B82F6',
    severity: 'MEDIA'
  },
  TALENTO_DESPERDICIADO: {
    name: { es: 'Talento Desperdiciado', en: 'Wasted Talent' },
    icon: TrendingUp,
    color: '#EAB308',
    severity: 'MEDIA'
  },
  RIESGO_AISLAMIENTO: {
    name: { es: 'Riesgo de Aislamiento', en: 'Isolation Risk' },
    icon: UserX,
    color: '#6B7280',
    severity: 'BAJA'
  }
};

// Level configurations
const LEVEL_COLORS = {
  CRITICO: 'bg-red-500 text-white',
  ALTO: 'bg-orange-500 text-white',
  ALTO_POTENCIAL: 'bg-green-500 text-white',
  MUY_ALTO: 'bg-green-500 text-white',
  MUY_ALTA: 'bg-green-500 text-white',
  POTENCIAL_SOLIDO: 'bg-blue-500 text-white',
  BUENO: 'bg-blue-500 text-white',
  MODERADO: 'bg-yellow-500 text-black',
  MEDIO: 'bg-yellow-500 text-black',
  MEDIA: 'bg-yellow-500 text-black',
  ACEPTABLE: 'bg-yellow-500 text-black',
  POTENCIAL_MODERADO: 'bg-yellow-500 text-black',
  BAJO: 'bg-green-500 text-white',
  BAJA: 'bg-green-500 text-white',
  POTENCIAL_LIMITADO: 'bg-red-500 text-white',
  POBRE: 'bg-red-500 text-white',
  EXCELENTE: 'bg-green-500 text-white'
};

const LEVEL_TRANSLATIONS = {
  CRITICO:           { es: 'CRITICO',           en: 'CRITICAL' },
  ALTO:              { es: 'ALTO',              en: 'HIGH' },
  ALTO_POTENCIAL:    { es: 'ALTO POTENCIAL',    en: 'HIGH POTENTIAL' },
  MUY_ALTO:          { es: 'MUY ALTO',          en: 'VERY HIGH' },
  MUY_ALTA:          { es: 'MUY ALTA',          en: 'VERY HIGH' },
  POTENCIAL_SOLIDO:  { es: 'POTENCIAL SOLIDO',  en: 'SOLID POTENTIAL' },
  BUENO:             { es: 'BUENO',             en: 'GOOD' },
  MODERADO:          { es: 'MODERADO',          en: 'MODERATE' },
  MEDIO:             { es: 'MEDIO',             en: 'MEDIUM' },
  MEDIA:             { es: 'MEDIA',             en: 'MEDIUM' },
  ACEPTABLE:         { es: 'ACEPTABLE',         en: 'ACCEPTABLE' },
  POTENCIAL_MODERADO:{ es: 'POTENCIAL MODERADO',en: 'MODERATE POTENTIAL' },
  BAJO:              { es: 'BAJO',              en: 'LOW' },
  BAJA:              { es: 'BAJA',              en: 'LOW' },
  POTENCIAL_LIMITADO:{ es: 'POTENCIAL LIMITADO',en: 'LIMITED POTENTIAL' },
  POBRE:             { es: 'POBRE',             en: 'POOR' },
  EXCELENTE:         { es: 'EXCELENTE',         en: 'EXCELLENT' },
};

const translateLevel = (level, lang) => {
  if (!level) return '';
  const t = LEVEL_TRANSLATIONS[level];
  return t ? t[lang] || t.es : level.replace(/_/g, ' ');
};

// Indicator Score Card Component
const IndicatorCard = ({ indicator, value, level, compact = false, drivers = [] }) => {
  const { language } = useLanguage();
  const [showDrivers, setShowDrivers] = useState(false);
  const config = INDICATOR_CONFIG[indicator];
  if (!config) return null;

  const Icon = config.icon;
  const isGood = config.invertedScale ? value < 40 : value >= 60;
  const barColor = isGood ? config.colorGood : config.colorBad;
  const levelColor = LEVEL_COLORS[level] || 'bg-gray-500 text-white';
  const levelLabel = translateLevel(level, language);

  if (compact) {
    return (
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: barColor }} />
          <span className="text-sm font-medium">{config.shortName[language]}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: barColor }}>{value}%</span>
          <Badge className={`text-xs ${levelColor}`}>
            {levelLabel}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-l-4" style={{ borderLeftColor: barColor }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: barColor }} />
            <span className="font-semibold text-sm">{config.name[language]}</span>
          </div>
          <Badge className={levelColor}>
            {levelLabel}
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all"
              style={{ width: `${value}%`, backgroundColor: barColor }}
            />
          </div>
          <span className="text-lg font-bold" style={{ color: barColor }}>{value}%</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">{config.description[language]}</p>
        
        {/* Drivers — why this result */}
        {drivers && drivers.length > 0 && (
          <div className="mt-2">
            <button 
              onClick={() => setShowDrivers(!showDrivers)}
              className="text-xs font-medium text-teal-700 hover:text-teal-900 flex items-center gap-1 cursor-pointer"
              data-testid={`drivers-toggle-${indicator}`}
            >
              <Info className="h-3 w-3" />
              {language === 'es' ? 'Por que este resultado?' : 'Why this result?'}
              <span className="text-[10px]">{showDrivers ? '\u25B2' : '\u25BC'}</span>
            </button>
            {showDrivers && (
              <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-gray-200" data-testid={`drivers-list-${indicator}`}>
                {drivers.map((d, i) => {
                  const effectColor = d.effect === 'green' ? '#22c55e' : d.effect === 'red' ? '#ef4444' : '#9ca3af';
                  const signalText = typeof d.signal === 'object' ? (d.signal[language] || d.signal.es) : d.signal;
                  const detailText = typeof d.detail === 'object' ? (d.detail[language] || d.detail.es) : d.detail;
                  return (
                    <div key={i} className="ml-1">
                      <div className="flex items-center gap-1.5">
                        <span style={{ color: effectColor, fontSize: '8px' }}>{'\u25CF'}</span>
                        <span className="text-xs font-medium text-gray-700">{signalText}</span>
                      </div>
                      {detailText && <p className="text-[11px] text-gray-500 ml-3.5">{detailText}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Company Summary Component - Risk counters based on alert severity
const CompanySummary = ({ data, alerts = [], onViewEmployeeReport }) => {
  const { language } = useLanguage();
  if (!data || !data.summary) return null;

  const { summary, employees_count, assessed_count, employees } = data;
  const ipsData = summary?.ips;
  
  // Count unique employees by alert severity (independently)
  const employeesWithHigh = new Set();
  const employeesWithMedium = new Set();
  
  alerts.forEach(alert => {
    const empId = alert.usuario_id;
    if (!empId) return;
    const severity = alert.severity || 'BAJA';
    
    if (severity === 'ALTA' || severity === 'CRITICA') {
      employeesWithHigh.add(empId);
    } else if (severity === 'MEDIA') {
      employeesWithMedium.add(empId);
    }
  });
  
  // Count is the size of each set (unique employees per severity)
  const highRiskCount = employeesWithHigh.size;
  const mediumRiskCount = employeesWithMedium.size;
  
  // Get at-risk employees list (IPS Crítico or Alto Riesgo)
  const atRiskEmployees = employees?.filter(emp => 
    emp.ips_level === 'CRITICO' || emp.ips_level === 'ALTO_RIESGO'
  ) || [];
  
  // Get indicator averages for display
  const indicatorAverages = summary?.averages || {};
  
  // Indicator display config with icons and names (without "Índice")
  const indicatorConfig = {
    irr: { 
      name: { es: 'Riesgo de Rotación', en: 'Turnover Risk' }, 
      icon: UserMinus,
      inverted: true // Low is good
    },
    ico: { 
      name: { es: 'Compromiso', en: 'Commitment' }, 
      icon: Handshake,
      inverted: false 
    },
    iap: { 
      name: { es: 'Adecuación al Puesto', en: 'Job Fit' }, 
      icon: Crosshair,
      inverted: false 
    },
    ipc: { 
      name: { es: 'Potencial de Crecimiento', en: 'Growth Potential' }, 
      icon: Sprout,
      inverted: false 
    },
    icf: { 
      name: { es: 'Cultural Fit', en: 'Cultural Fit' }, 
      icon: Palette,
      inverted: false 
    },
    ipe: { 
      name: { es: 'Productividad', en: 'Productivity' }, 
      icon: Gauge,
      inverted: false 
    },
    ilo: { 
      name: { es: 'Lealtad', en: 'Loyalty' }, 
      icon: Award,
      inverted: false 
    }
  };
  
  // Get color and status based on value
  const getIndicatorStyle = (key, value) => {
    const config = indicatorConfig[key];
    const isInverted = config?.inverted;
    
    // Determine if value is good, medium, or bad
    let status; // 'good', 'medium', 'bad'
    if (isInverted) {
      // For IRR: low is good
      status = value < 40 ? 'good' : value < 60 ? 'medium' : 'bad';
    } else {
      // For others: high is good
      status = value >= 70 ? 'good' : value >= 50 ? 'medium' : 'bad';
    }
    
    const styles = {
      good: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', StatusIcon: Check, statusColor: 'text-green-500' },
      medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', StatusIcon: AlertOctagon, statusColor: 'text-yellow-500' },
      bad: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', StatusIcon: X, statusColor: 'text-red-500' }
    };
    
    return styles[status];
  };
  
  return (
    <div className="space-y-4">
      {/* IPS Summary - Only show if IPS data available */}
      {ipsData && ipsData.score !== null && (
        <IPSSummary
          ips={ipsData.score}
          ipsLevel={ipsData.level}
          employeeCount={assessed_count || 0}
          distribution={ipsData.distribution}
          distributionPercentage={ipsData.distribution_percentage}
          atRiskCount={ipsData.at_risk_count}
          healthyCount={ipsData.healthy_count}
          minIps={employees?.length > 0 ? Math.min(...employees.map(e => e.ips || 0)) : 0}
          maxIps={employees?.length > 0 ? Math.max(...employees.map(e => e.ips || 100)) : 100}
          atRiskEmployees={atRiskEmployees}
          onViewEmployeeReport={onViewEmployeeReport}
        />
      )}
      
      {/* HR Indicators Grid - With icons and status */}
      {Object.keys(indicatorAverages).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo'].map(key => {
            const value = indicatorAverages[key];
            if (value === undefined) return null;
            
            const config = indicatorConfig[key];
            const style = getIndicatorStyle(key, value);
            const IndicatorIcon = config?.icon || Activity;
            const StatusIcon = style.StatusIcon;
            
            return (
              <div 
                key={key} 
                className={`rounded-xl p-3 text-center border ${style.bg} ${style.border}`}
              >
                {/* Row 1: Indicator Icon */}
                <div className="flex justify-center mb-2">
                  <IndicatorIcon className={`h-5 w-5 ${style.text}`} />
                </div>
                
                {/* Row 2: Value + Status Icon */}
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className={`text-xl font-bold ${style.text}`}>
                    {value?.toFixed?.(1) || value}%
                  </span>
                  <StatusIcon className={`h-4 w-4 ${style.statusColor}`} />
                </div>
                
                {/* Row 3: Name */}
                <p className={`text-xs ${style.text} opacity-80 leading-tight`}>
                  {config?.name?.[language] || key}
                </p>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Stats Counters - After indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <Users className="h-5 w-5 text-blue-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-blue-700">{employees_count || 0}</p>
          <p className="text-xs text-blue-600">{language === 'es' ? 'Total' : 'Total'}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <UserCheck className="h-5 w-5 text-green-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-green-700">{assessed_count || 0}</p>
          <p className="text-xs text-green-600">{language === 'es' ? 'Evaluados' : 'Assessed'}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <AlertTriangle className="h-5 w-5 text-red-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-red-700">{highRiskCount}</p>
          <p className="text-xs text-red-600">{language === 'es' ? 'Riesgo Alto' : 'High Risk'}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3 text-center">
          <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-yellow-700">{mediumRiskCount}</p>
          <p className="text-xs text-yellow-600">{language === 'es' ? 'Riesgo Medio' : 'Medium Risk'}</p>
        </div>
      </div>
    </div>
  );
};

// Red Flags Component - Collapsed by default with +/- toggle
const RedFlagsSection = ({ alerts, onViewReport }) => {
  const { language } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  
  if (!alerts || alerts.length === 0) {
    return (
      <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
        <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
        <p className="text-sm text-green-700">
          {language === 'es' ? 'No hay alertas activas' : 'No active alerts'}
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Collapsible Header with +/- button */}
      <div 
        className="flex items-center justify-between p-3 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <span className="font-semibold text-sm text-orange-700">
            {language === 'es' ? 'Alertas Activas' : 'Active Alerts'}
          </span>
          <Badge variant="destructive" className="text-xs">{alerts.length}</Badge>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
          {expanded ? (
            <span className="text-lg font-bold text-orange-600">−</span>
          ) : (
            <span className="text-lg font-bold text-orange-600">+</span>
          )}
        </Button>
      </div>
      
      {/* Collapsible Content */}
      {expanded && (
        <div className="p-3 space-y-2 bg-white">
          {alerts.map((alert, idx) => {
            const config = ALERT_CONFIG[alert.alert_type] || {};
            const Icon = config.icon || AlertTriangle;
            const employeeName = alert.usuario_nombre || alert.employee_name;
            return (
              <div 
                key={idx}
                className="flex items-start gap-3 p-3 rounded-lg border"
                style={{ borderColor: config.color, backgroundColor: `${config.color}10` }}
              >
                <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: config.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm" style={{ color: config.color }}>
                      {config.name?.[language] || alert.alert_type}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {alert.severity || config.severity}
                    </Badge>
                  </div>
                  
                  {/* Employee name prominently displayed */}
                  {employeeName && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-medium text-gray-800">
                        👤 {employeeName}
                      </span>
                      {alert.usuario_email && (
                        <span className="text-xs text-gray-500">
                          ({alert.usuario_email})
                        </span>
                      )}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-600 mt-1">
                    {language === 'es' ? alert.recommended_action_es : alert.recommended_action_en}
                  </p>
                  
                  {/* View Report Button */}
                  {(alert.usuario_id || employeeName) && onViewReport && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); onViewReport(alert); }}
                      style={{ borderColor: config.color, color: config.color }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      {language === 'es' ? 'Ver Reporte' : 'View Report'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Employee Indicators Row
const EmployeeIndicatorRow = ({ employee, indicators, onClick }) => {
  const { language } = useLanguage();
  // Get IPS data from employee object (now included from backend)
  const ips = employee.ips;
  const ipsLevel = employee.ips_level;
  const ipsColor = employee.ips_color;
  
  // IPS color helper
  const getIPSColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    if (score >= 30) return 'text-orange-600';
    return 'text-red-600';
  };
  
  return (
    <div 
      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
      onClick={() => onClick?.(employee)}
      data-testid={`employee-row-${employee.user_id || employee.id}`}
    >
      <div className="flex items-center gap-3">
        <div>
          <p className="font-medium text-sm">{employee.nombre}</p>
          <p className="text-xs text-gray-500">{employee.departamento || employee.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* IPS Badge - Prominent display */}
        {ips !== undefined && (
          <div 
            className="text-center px-3 py-1 rounded-full"
            style={{ backgroundColor: `${ipsColor}20` }}
            data-testid={`ips-badge-${employee.user_id || employee.id}`}
          >
            <p className="text-xs text-gray-500 font-medium">IPS</p>
            <p className={`text-sm font-bold ${getIPSColor(ips)}`}>
              {ips?.toFixed(1)}
            </p>
          </div>
        )}
        
        {indicators && (
          <>
            {indicators.irr !== undefined && (
              <div className="text-center px-2">
                <p className="text-xs text-gray-500">IRR</p>
                <p className={`text-sm font-bold ${indicators.irr >= 60 ? 'text-red-600' : indicators.irr >= 40 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {indicators.irr}%
                </p>
              </div>
            )}
            {indicators.icf !== undefined && (
              <div className="text-center px-2">
                <p className="text-xs text-gray-500">ICF</p>
                <p className={`text-sm font-bold ${indicators.icf >= 70 ? 'text-green-600' : indicators.icf >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {indicators.icf}%
                </p>
              </div>
            )}
            {indicators.ipc !== undefined && (
              <div className="text-center px-2">
                <p className="text-xs text-gray-500">IPC</p>
                <p className={`text-sm font-bold ${indicators.ipc >= 70 ? 'text-green-600' : indicators.ipc >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {indicators.ipc}%
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Main HR Indicators Panel Component
 * @param {string} context - 'culture', 'personality', or 'integrated'
 * @param {array} indicatorsToShow - Array of indicator keys to display (e.g., ['icf', 'iap', 'ipe'])
 * @param {boolean} showAlerts - Whether to show red flags section
 * @param {boolean} showEmployees - Whether to show employees list
 * @param {boolean} showCandidates - Whether to show candidates list
 * @param {function} onViewAlertReport - Callback when user clicks "View Report" on an alert
 * @param {function} onViewEmployeeReport - Callback when user clicks "View Report" on an employee
 */
const HRIndicatorsPanel = ({ 
  context = 'integrated',
  indicatorsToShow = ['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo'],
  showAlerts = true,
  showEmployees = true,
  showCandidates = false,
  title,
  description,
  onViewAlertReport,
  onViewEmployeeReport,
  filterUserId = null,        // Filter alerts by specific user ID
  filterDepartment = null     // Filter alerts by department name
}) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personIndicators, setPersonIndicators] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    if (user?.empresa_id) {
      loadData();
    }
  }, [user, context, filterUserId, filterDepartment]);

  const loadData = async () => {
    setLoading(true);
    try {
      let indicatorsRes;
      let alertSource;
      
      // Use different endpoints based on context
      switch (context) {
        case 'culture':
          // Culture indicators - based on Dimensions only
          indicatorsRes = await axios.get(`/v2/hr-indicators/company/${user.empresa_id}?source=dimensions`);
          alertSource = 'dimensions';
          break;
        case 'personality':
          // Personality indicators - based on Enneagram only
          indicatorsRes = await axios.get(`/v2/hr-indicators/company/${user.empresa_id}?source=enneagram`);
          alertSource = 'enneagram';
          break;
        case 'integrated':
        default:
          // Integrated indicators - combining both sources
          indicatorsRes = await axios.get(`/v2/hr-indicators/company/${user.empresa_id}?source=integrated`);
          alertSource = null; // Show all alerts in integrated view
          break;
      }
      
      if (indicatorsRes.data.success) {
        setData(indicatorsRes.data.data);
      }

      // Load risk alerts if needed - filtered by source
      if (showAlerts) {
        const alertsUrl = alertSource 
          ? `/v2/risk-alerts/company/${user.empresa_id}?source=${alertSource}`
          : `/v2/risk-alerts/company/${user.empresa_id}`;
        const alertsRes = await axios.get(alertsUrl);
        if (alertsRes.data.success) {
          let filteredAlerts = alertsRes.data.data || [];
          
          // Apply user filter if specified
          if (filterUserId) {
            filteredAlerts = filteredAlerts.filter(a => 
              a.usuario_id === filterUserId || 
              String(a.usuario_id) === String(filterUserId)
            );
          }
          
          // Apply department filter if specified
          if (filterDepartment) {
            filteredAlerts = filteredAlerts.filter(a => 
              a.departamento === filterDepartment ||
              a.usuario_departamento === filterDepartment
            );
          }
          
          setAlerts(filteredAlerts);
        }
      }
    } catch (error) {
      logger.error('Error loading HR indicators:', error);
      // Silently fail - indicators are optional
    } finally {
      setLoading(false);
    }
  };

  const loadPersonIndicators = async (person) => {
    setSelectedPerson(person);
    if (!person.assessment_id) {
      setPersonIndicators(null);
      return;
    }
    
    try {
      const res = await axios.get(`/v2/hr-indicators/${person.assessment_id}`);
      if (res.data.success) {
        setPersonIndicators(res.data.data);
      }
    } catch (error) {
      logger.error('Error loading person indicators:', error);
      setPersonIndicators(null);
    }
  };

  const handleRecalculate = async () => {
    if (!user?.empresa_id) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`/v2/hr-indicators/recalculate-company/${user.empresa_id}`);
      if (res.data.success) {
        const { processed, errors } = res.data.data;
        toast.success(
          language === 'es' 
            ? `Recalculados ${processed} indicadores${errors > 0 ? ` (${errors} errores)` : ''}`
            : `Recalculated ${processed} indicators${errors > 0 ? ` (${errors} errors)` : ''}`
        );
        await loadData();
      } else {
        toast.error(res.data.message || 'Error');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al recalcular indicadores' : 'Error recalculating indicators');
    } finally {
      setRecalculating(false);
    }
  };

  // Get title based on context
  const getTitle = () => {
    if (title) return title;
    switch (context) {
      case 'culture':
        return language === 'es' ? 'Indicadores de Cultura' : 'Culture Indicators';
      case 'personality':
        return language === 'es' ? 'Indicadores de Personalidad' : 'Personality Indicators';
      default:
        return language === 'es' ? 'Indicadores de RH' : 'HR Indicators';
    }
  };

  const getDescription = () => {
    if (description) return description;
    switch (context) {
      case 'culture':
        return language === 'es' 
          ? 'Métricas basadas en las 10 Dimensiones de Cultura Organizacional'
          : 'Metrics based on the 10 Organizational Culture Dimensions';
      case 'personality':
        return language === 'es'
          ? 'Métricas derivadas del perfil de Eneagrama'
          : 'Metrics derived from the Enneagram profile';
      default:
        return language === 'es'
          ? 'Análisis integral combinando Cultura y Personalidad'
          : 'Comprehensive analysis combining Culture and Personality';
    }
  };

  // No access check - component assumes parent already checked
  if (loading) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2 text-gray-400" />
          <span className="text-gray-500">
            {language === 'es' ? 'Cargando indicadores...' : 'Loading indicators...'}
          </span>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.employees || data.employees.length === 0) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-base">
            <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
            {getTitle()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-gray-500">
            <Info className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">
              {language === 'es' 
                ? 'No hay suficientes datos para calcular indicadores. Se requieren evaluaciones completadas.'
                : 'Not enough data to calculate indicators. Completed assessments required.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter indicators based on props
  const averages = data.summary?.averages || {};
  const levels = data.summary?.levels || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center text-base">
              <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
              {getTitle()}
            </CardTitle>
            <CardDescription>{getDescription()}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {(user?.rol === 'super_admin' || user?.rol === 'company_admin') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRecalculate}
                disabled={recalculating}
                data-testid="recalculate-indicators-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {recalculating ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Activity className="h-4 w-4 mr-1" />
                )}
                {language === 'es' ? 'Recalcular' : 'Recalculate'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-indicators-btn">
              <RefreshCw className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Actualizar' : 'Refresh'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Company Summary - includes IPS donut chart and HR Indicators grid */}
        <CompanySummary data={data} alerts={alerts} onViewEmployeeReport={onViewEmployeeReport} />

        {/* Red Flags - Collapsible Section */}
        {showAlerts && (
          <RedFlagsSection 
            alerts={alerts.filter(a => !a.is_acknowledged)} 
            onViewReport={onViewAlertReport}
          />
        )}

        {/* Employees List with Indicators */}
        {showEmployees && data.employees && data.employees.length > 0 && (
          <div>
            <div 
              className="flex items-center justify-between cursor-pointer py-2"
              onClick={() => setExpanded(!expanded)}
            >
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Users className="h-4 w-4" style={{ color: '#1A878D' }} />
                {language === 'es' ? 'Empleados Evaluados' : 'Assessed Employees'}
                <Badge variant="secondary" className="text-xs">{data.employees.length}</Badge>
              </h4>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            
            {expanded && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.employees.map((emp) => (
                  <EmployeeIndicatorRow
                    key={emp.user_id || emp.id}
                    employee={emp}
                    indicators={emp.indicators}
                    onClick={loadPersonIndicators}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Selected Person Details Modal/Expanded View */}
        {selectedPerson && personIndicators && (
          <Card className="border-2 border-teal-500 bg-teal-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  📊 {selectedPerson.nombre}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedPerson(null); setPersonIndicators(null); }}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {indicatorsToShow.map(key => {
                  const value = personIndicators.indicators?.[key];
                  const level = personIndicators.levels?.[`${key}_level`];
                  if (value === undefined) return null;
                  return (
                    <IndicatorCard
                      key={key}
                      indicator={key}
                      value={value}
                      level={level}
                      compact={true}
                    />
                  );
                })}
              </div>
              {personIndicators.red_flags && personIndicators.red_flags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-2 text-orange-700">
                    {language === 'es' ? 'Alertas Detectadas:' : 'Detected Alerts:'}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {personIndicators.red_flags.map((flag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-orange-100 text-orange-700">
                        {ALERT_CONFIG[flag.alert_type]?.name?.[language] || flag.alert_type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default HRIndicatorsPanel;
